import React, { useState, useEffect } from 'react';
import moment from 'moment';
import _ from 'lodash';
import { WAIVER_MATCH_CRITERIA } from './WaiverMatchCriteria';
import { Waiver } from '../../js/headersegment/interfaces';

interface MatchCriteria {
  [key: string]: string;
}

interface WaiverMatchCriteria {
  type: string;
  name: string;
  match: string;
  sprite: string;
  title: string;
  description: string;
}

interface WaiverBulletinProps {
  waiver: Waiver;
  matchCriteria: MatchCriteria;
}

const DATE_FORMAT = 'DD-MMM-YYYY';

const WaiverBulletin: React.FC<WaiverBulletinProps> = ({ waiver, matchCriteria }) => {
  const [matchData, setMatchData] = useState<WaiverMatchCriteria[]>([]);
  const [selectedMatch, setSelectedMatch] = useState<WaiverMatchCriteria | null>(null);
  const [displayedMatch, setDisplayedMatch] = useState<WaiverMatchCriteria | null>(null);

  useEffect(() => {
    buildMatchData();
  }, [waiver, matchCriteria]);

  const buildMatchData = () => {
    const foundTypes: string[] = [];
    const foundMatches: WaiverMatchCriteria[] = [];
    _.forEach(WAIVER_MATCH_CRITERIA, criteria => {
      if (
        !_.includes(foundTypes, criteria.type) &&
        matchCriteria[criteria.name] === criteria.match
      ) {
        foundTypes.push(criteria.type);
        foundMatches.push(criteria);
      }
    });
    setMatchData(foundMatches);
    setSelectedMatch(foundMatches[0]);
    setDisplayedMatch(foundMatches[0]);
  };

  const travelDates = () => {
    if (!waiver.firstEffectiveDate) {
      return 'N/A';
    }
    const startDate = moment(waiver.firstEffectiveDate).format(DATE_FORMAT);
    const endDate = waiver.lastEffectiveDate
      ? moment(waiver.lastEffectiveDate).format(DATE_FORMAT)
      : 'onwards';
    return startDate === endDate ? startDate : `${startDate} - ${endDate}`;
  };

  const regionType = () => (isCountryWaiver() ? 'Countries' : 'Airports');

  const isCountryWaiver = () => _.size(waiver.countryCodes) > 0;

  const regions = () => {
    if (waiver.systemWide) {
      return 'SYSTEM-WIDE';
    }
    if (isCountryWaiver()) {
      return (waiver.countryCodes || []).join(', ');
    }
    return _.isArray(waiver.airportCodes)
      ? waiver.airportCodes.join(', ')
      : '';
  };

  return (
    <div className="waiver-bulletin">
      <div className="bulletin-body row">
        <div className="bulletin-content-wrapper col-md-6 row">
          <div className="bulletin-title row">Applicable Waiver</div>
          <div className="bulletin-content row no-gutter">
            <div className="bulletin-item col-md-6">
              <div className="bulletin-name">Airline</div>
              <div className="bulletin-value">{waiver.issuingAirlineCode}</div>
            </div>
            <div className="bulletin-item col-md-6">
              <div className="bulletin-name">Affected Travel Dates</div>
              <div className="bulletin-value">{travelDates()}</div>
            </div>
          </div>
          <div className="bulletin-content row no-gutter">
            <div className="bulletin-item col-md-6">
              <div className="bulletin-name">Waiver Name</div>
              <div className="bulletin-value">{waiver.name}</div>
            </div>
            <div className="bulletin-item col-md-6">
              <div className="bulletin-name">Affected {regionType()}</div>
              <div className="bulletin-value">{regions()}</div>
            </div>
          </div>
        </div>
        <div className="bulletin-content-wrapper col-md-6 row">
          <div className="bulletin-criteria-header">
            <div className="bulletin-criteria-title">MATCHING CRITERIA</div>
            <div className="bulletin-criteria-icon-wrapper">
              {matchData.map(match => (
                <div
                  key={match.type}
                  className={`bulletin-criteria-icon icon-normal ${
                    displayedMatch?.type === match.type ? 'icon-selected' : ''
                  }`}
                  onClick={() => setSelectedMatch(match)}
                  onMouseOver={() => setDisplayedMatch(match)}
                  onMouseLeave={() => setDisplayedMatch(selectedMatch)}
                >
                  <div className={`sprites ${match.sprite}`}></div>
                  {match.match !== 'MATCH' && <div className="icon-evil-dot"></div>}
                  {displayedMatch?.type === match.type && <div className="icon-arrow"></div>}
                </div>
              ))}
            </div>
          </div>
          <div className="bulletin-criteria-box">
            <div className="bulletin-criteria-type">{displayedMatch?.title}</div>
            <div
              className="bulletin-criteria-description"
              dangerouslySetInnerHTML={{ __html: displayedMatch?.description || '' }}
            ></div>
          </div>
        </div>
      </div>
      <a href={`/waivers/${waiver.id}`}>
        <div className="bulletin-waiver-link">VIEW WAIVER DETAILS</div>
      </a>
    </div>
  );
};

export default WaiverBulletin;